import { useEffect, useRef, useState } from "react";
import { NavLink, Navigate, Outlet, useSearchParams } from "react-router";
import { UserAvatar } from "@/components/nav/user-avatar";
import { Button } from "@/components/ui/button";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { BillingPanel } from "@/features/billing/billing-page";
import { OrgAdminOverviewPage } from "@/features/org-admin/org-admin-pages";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { cn } from "@/lib/cn";
import { useOrg } from "@/lib/org-context";
import { useTheme, type ThemeMode } from "@/lib/theme";

const AVATAR_MAX_EDGE = 256;
const AVATAR_JPEG_QUALITY = 0.88;

const tabs = [
  { to: "profile", label: "Profile & Appearance" },
  { to: "security", label: "Security" },
  { to: "workspace", label: "Workspace" },
  { to: "billing", label: "Billing" },
] as const;

const workspaceLinks = [
  { to: "", label: "Overview", end: true },
  { to: "usage", label: "Usage" },
  { to: "audit", label: "Audit Log" },
  { to: "settings", label: "Workspace Settings" },
] as const;

async function fileToAvatarPayload(
  file: File,
): Promise<{ dataBase64: string; contentType: string }> {
  if (!file.type.startsWith("image/")) {
    throw new Error("Choose a JPEG, PNG, WebP, or GIF image.");
  }
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(
    1,
    AVATAR_MAX_EDGE / Math.max(bitmap.width, bitmap.height),
  );
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not prepare the image.");
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) =>
        result
          ? resolve(result)
          : reject(new Error("Could not encode the image.")),
      "image/jpeg",
      AVATAR_JPEG_QUALITY,
    );
  });
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return { dataBase64: btoa(binary), contentType: "image/jpeg" };
}

export function SettingsPage() {
  const base = "/settings";

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-rs-ink">Settings</h2>
        <p className="text-sm text-rs-ink-muted">
          Profile, workspace, and billing for this account.
        </p>
      </div>

      <div
        className="flex flex-wrap gap-1 border-b border-rs-border pb-2"
        role="tablist"
        aria-label="Settings sections"
      >
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={`${base}/${tab.to}`}
            role="tab"
            className={({ isActive }) =>
              cn(
                "inline-flex h-8 items-center rounded-rs-sm px-3 text-sm font-medium text-rs-ink-muted hover:bg-rs-muted hover:text-rs-ink",
                isActive && "bg-rs-accent-soft text-rs-accent-ink",
              )
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </div>

      <Outlet />

      <p className="pt-4 text-xs text-rs-ink-muted">
        <a
          href="/legal/privacy.html"
          target="_blank"
          rel="noreferrer"
          className="underline-offset-2 hover:underline"
        >
          Privacy Policy
        </a>
        <span aria-hidden="true"> · </span>
        <a
          href="/legal/terms.html"
          target="_blank"
          rel="noreferrer"
          className="underline-offset-2 hover:underline"
        >
          Terms of Service
        </a>
      </p>
    </div>
  );
}

export function SettingsIndexRedirect() {
  return <Navigate to="profile" replace />;
}

export function SettingsProfileTab() {
  const { user, refresh } = useAuth();
  const { mode, setMode } = useTheme();
  const fileRef = useRef<HTMLInputElement>(null);
  const [avatarPending, setAvatarPending] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  async function onPickAvatar(file: File | null) {
    if (!file) return;
    setAvatarError(null);
    setAvatarPending(true);
    try {
      const payload = await fileToAvatarPayload(file);
      await api("/api/auth/avatar", {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      await refresh();
    } catch (error) {
      setAvatarError(
        error instanceof ApiError
          ? error.message
          : error instanceof Error
            ? error.message
            : "Could not upload photo.",
      );
    } finally {
      setAvatarPending(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function onRemoveAvatar() {
    setAvatarError(null);
    setAvatarPending(true);
    try {
      await api("/api/auth/avatar", { method: "DELETE" });
      await refresh();
    } catch (error) {
      setAvatarError(
        error instanceof ApiError ? error.message : "Could not remove photo.",
      );
    } finally {
      setAvatarPending(false);
    }
  }

  return (
    <div className="space-y-5">
      <Panel>
        <PanelHeader title="Profile" />
        <div className="space-y-4 px-5 py-4">
          <div className="flex flex-wrap items-center gap-4">
            <UserAvatar
              displayName={user?.displayName}
              email={user?.email}
              avatarUrl={user?.avatarUrl}
              size="lg"
            />
            <div className="min-w-0 flex-1 space-y-1">
              <p className="text-sm font-medium text-rs-ink">Profile photo</p>
              <p className="text-xs text-rs-ink-muted">
                Uploaded photos override Gravatar. Without an upload we use your
                Gravatar when available, otherwise initials.
              </p>
              <div className="flex flex-wrap gap-2 pt-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="sr-only"
                  onChange={(event) =>
                    void onPickAvatar(event.target.files?.[0] ?? null)
                  }
                />
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={avatarPending}
                  onClick={() => fileRef.current?.click()}
                >
                  {avatarPending ? "Working…" : "Upload Photo"}
                </Button>
                {user?.hasCustomAvatar ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={avatarPending}
                    onClick={() => void onRemoveAvatar()}
                  >
                    Remove Photo
                  </Button>
                ) : null}
              </div>
              {avatarError ? (
                <p className="text-xs text-rs-danger" role="alert">
                  {avatarError}
                </p>
              ) : null}
            </div>
          </div>

          <dl className="space-y-3 border-t border-rs-border pt-4 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-rs-ink-muted">Name</dt>
              <dd className="text-rs-ink">{user?.displayName}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-rs-ink-muted">Email</dt>
              <dd className="text-rs-ink">{user?.email}</dd>
            </div>
          </dl>
        </div>
      </Panel>

      <ConnectedAccountsPanel />

      <Panel>
        <PanelHeader
          title="Appearance"
          description="Light, dark, or match system preference"
        />
        <div className="flex flex-wrap gap-2 px-5 py-4">
          {(["light", "dark", "system"] as ThemeMode[]).map((m) => (
            <Button
              key={m}
              size="sm"
              variant={mode === m ? "primary" : "secondary"}
              onClick={() => setMode(m)}
            >
              {m}
            </Button>
          ))}
        </div>
      </Panel>
    </div>
  );
}

/** @deprecated Prefer SettingsWorkspaceTab — kept as alias for legacy imports. */
export function SettingsOrganizationTab() {
  return <SettingsWorkspaceTab />;
}

type SsoLinks = {
  googleLinked: boolean;
  zoomLinked: boolean;
  microsoftLinked: boolean;
  emailVerified: boolean;
};

const LINK_RESULT_MESSAGES: Record<string, { ok: boolean; text: string }> = {
  connected_google: { ok: true, text: "Google sign-in connected." },
  connected_zoom: { ok: true, text: "Zoom sign-in connected." },
  connected_microsoft: { ok: true, text: "Microsoft sign-in connected." },
  link_signed_out: {
    ok: false,
    text: "You were signed out before the connection finished. Sign in and try again.",
  },
  link_email_not_verified: {
    ok: false,
    text: "Verify your email before connecting a sign-in method.",
  },
  link_already_linked: {
    ok: false,
    text: "That account is already connected to another AttendeeSync user.",
  },
};

/**
 * Explicit Connect Google/Zoom/Microsoft linking (supported SSO merge path).
 * Connect actions are available only to signed-in, email-verified users.
 */
function ConnectedAccountsPanel() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [links, setLinks] = useState<SsoLinks | null>(null);
  const [linksError, setLinksError] = useState<string | null>(null);
  const [resendState, setResendState] = useState<string | null>(null);
  const [resendBusy, setResendBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLinksError(null);
    void api<SsoLinks>("/api/auth/sso-links")
      .then((status) => {
        if (!cancelled) setLinks(status);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLinksError(
            err instanceof ApiError
              ? err.message
              : "Could not load connected accounts.",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const resultKey = params.get("connected")
    ? `connected_${params.get("connected")}`
    : params.get("link")
      ? `link_${params.get("link")}`
      : null;
  const result = resultKey ? LINK_RESULT_MESSAGES[resultKey] : undefined;

  useEffect(() => {
    if (!resultKey) return;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("connected");
        next.delete("link");
        return next;
      },
      { replace: true },
    );
    // Clear once so a refresh does not replay the banner. Runs once per key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultKey]);

  async function onResend() {
    if (!user?.email) return;
    setResendBusy(true);
    setResendState(null);
    try {
      await api("/api/auth/resend-verification", {
        method: "POST",
        body: JSON.stringify({ email: user.email }),
      });
      setResendState("Verification email sent. Check your inbox.");
    } catch (err) {
      setResendState(err instanceof Error ? err.message : "Could not resend.");
    } finally {
      setResendBusy(false);
    }
  }

  const verified = Boolean(user?.emailVerified);
  const googleLinked = Boolean(links?.googleLinked);
  const zoomLinked = Boolean(links?.zoomLinked);
  const microsoftLinked = Boolean(links?.microsoftLinked);

  return (
    <Panel>
      <PanelHeader
        title="Connected accounts"
        description="Link Google, Zoom, or Microsoft as an extra way to sign in. Available after email verification."
      />
      <div className="space-y-4 px-5 py-4">
        {result ? (
          <p
            className={
              result.ok ? "text-sm text-rs-ink" : "text-sm text-rs-danger"
            }
            role={result.ok ? "status" : "alert"}
          >
            {result.text}
          </p>
        ) : null}
        {!verified ? (
          <div className="space-y-2">
            <p className="text-sm text-rs-danger" role="alert">
              Your email isn&apos;t verified yet. Verify it before connecting a
              sign-in method.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={resendBusy}
                onClick={() => void onResend()}
              >
                {resendBusy ? "Sending…" : "Resend verification email"}
              </Button>
              {resendState ? (
                <span className="text-xs text-rs-ink-muted" role="status">
                  {resendState}
                </span>
              ) : null}
            </div>
          </div>
        ) : null}
        {linksError ? (
          <p className="text-sm text-rs-danger" role="alert">
            {linksError}
          </p>
        ) : null}
        <ul className="space-y-3 text-sm">
          <li className="flex items-center justify-between gap-3">
            <div>
              <p className="font-medium text-rs-ink">Google</p>
              <p className="text-xs text-rs-ink-muted">
                {googleLinked ? "Connected" : "Not connected"}
              </p>
            </div>
            {!googleLinked && verified ? (
              <Button type="button" size="sm" variant="secondary" asChild>
                <a href="/oauth/login/google/start?link=1">Connect Google</a>
              </Button>
            ) : null}
          </li>
          <li className="flex items-center justify-between gap-3">
            <div>
              <p className="font-medium text-rs-ink">Zoom</p>
              <p className="text-xs text-rs-ink-muted">
                {zoomLinked ? "Connected" : "Not connected"}
              </p>
            </div>
            {!zoomLinked && verified ? (
              <Button type="button" size="sm" variant="secondary" asChild>
                <a href="/oauth/login/start?link=1">Connect Zoom</a>
              </Button>
            ) : null}
          </li>
          <li className="flex items-center justify-between gap-3">
            <div>
              <p className="font-medium text-rs-ink">Microsoft</p>
              <p className="text-xs text-rs-ink-muted">
                {microsoftLinked ? "Connected" : "Not connected"}
              </p>
            </div>
            {!microsoftLinked && verified ? (
              <Button type="button" size="sm" variant="secondary" asChild>
                <a href="/oauth/login/microsoft/start?link=1">
                  Connect Microsoft
                </a>
              </Button>
            ) : null}
          </li>
        </ul>
      </div>
    </Panel>
  );
}

export function SettingsWorkspaceTab() {
  const { organization, canManage } = useOrg();
  const base = "/settings/workspace";

  if (!canManage) {
    return <Outlet />;
  }

  return (
    <div className="rs-org-admin flex flex-col gap-6 md:flex-row md:gap-8">
      <aside className="rs-org-nav w-full shrink-0 md:w-48">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-rs-ink-subtle">
          {organization?.name}
        </p>
        <nav
          className="flex gap-1 overflow-x-auto md:flex-col md:gap-0 md:overflow-visible"
          aria-label="Workspace"
        >
          {workspaceLinks.map((link) => (
            <NavLink
              key={link.to || "overview"}
              to={link.to ? `${base}/${link.to}` : base}
              end={"end" in link ? link.end : false}
              className={({ isActive }) =>
                cn(
                  "block shrink-0 rounded-rs-sm px-3 py-2 text-sm text-rs-ink-muted hover:bg-rs-muted hover:text-rs-ink",
                  isActive &&
                    "bg-rs-accent-soft font-medium text-rs-accent-ink",
                )
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="min-w-0 flex-1">
        <Outlet />
      </div>
    </div>
  );
}

/** @deprecated Prefer SettingsWorkspaceIndex. */
export function SettingsOrganizationIndex() {
  return <SettingsWorkspaceIndex />;
}

export function SettingsWorkspaceIndex() {
  const { organization, role, canManage } = useOrg();

  if (!canManage) {
    return (
      <div className="space-y-5">
        <Panel>
          <PanelHeader title="Workspace" />
          <dl className="space-y-3 px-5 py-4 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-rs-ink-muted">Name</dt>
              <dd className="text-rs-ink">{organization?.name}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-rs-ink-muted">Your role</dt>
              <dd className="capitalize text-rs-ink">{role}</dd>
            </div>
          </dl>
        </Panel>
      </div>
    );
  }

  return <OrgAdminOverviewPage />;
}

export function SettingsBillingTab() {
  return <BillingPanel />;
}
